/**
 * Organization invitations list.
 *
 * ── Tenancy mode: the invitation that CANNOT be accepted ─────────────────────
 *
 * In "single academy" mode the server denies joining a SECOND academy
 * (`tenancy::ensure_can_join_org`, applied inside `accept_invitation`). The
 * interface didn't anticipate it: the invitation was listed with its *Aceptar* button
 * and the click returned a dry 403. The diagnosis was noted in the tenancy
 * mode record (`2026-08-15-modo-academia-unica.md` §6/§8).
 *
 * It's closed where it belongs —whoever owns invitations— with the SAME predicate
 * the rest of the app uses, `canJoinAnotherOrganization`, against the user's
 * real number of academies. The criterion isn't re-derived here.
 *
 * **And the invitation isn't hidden: its "Aceptar" is removed.** Hiding it would leave
 * the person with an email the app doesn't recognize and, worse, unable to
 * reject it — because rejecting IS possible (`reject_invitation` doesn't go through the
 * mode check). So it's shown in a separate block that says why it can't be accepted and
 * offers only the way out the server allows. What disappears is the
 * impossible action, which was the defect.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, For, Show, untrack } from "solid-js";
import { fromActorRef, useActorRef } from "@fleet/state-machines/solid";
import { Archive, CheckCircle, Loader2, Mail, XCircle } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Badge, Card, CardContent, Skeleton } from "@fleet/ui";
import { api, createApiQuery, fetchApi } from "@/lib/api";
import {
  canJoinAnotherOrganization,
  type components,
  getInvitationMachineLogic,
  queryKeys,
} from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";

type Invitation = components["schemas"]["InvitationDTO"];

export function InvitationList() {
  const { t } = useI18n();
  const { orgs } = useActiveOrg();
  const invitations = createApiQuery<Invitation[] | null, components["schemas"]["ErrorModel"]>(
    () => queryKeys.auth.invitations(),
    async () => api.GET("/api/org/listInvitations", {}),
  );

  /**
   * Can this user join ANOTHER academy? Exact mirror of the server's
   * guard: the mode, against the number of live memberships.
   */
  const canJoin = createMemo(() => canJoinAnotherOrganization(orgs().length));

  const allPending = createMemo(
    () => invitations.data?.filter((inv) => inv.status === "pending") ?? [],
  );

  /** Pending ones the server WOULD accept. They're the only ones with "Aceptar". */
  const pendingInvitations = createMemo(() => (canJoin() ? allPending() : []));

  /** Pending ones the server would reject: they can only be dismissed. */
  const unacceptableInvitations = createMemo(() => (canJoin() ? [] : allPending()));

  const historyInvitations = createMemo(
    () => invitations.data?.filter((inv) => inv.status !== "pending") ?? [],
  );

  const handleAccept = async (id: string) => {
    await fetchApi(api.POST("/api/org/acceptInvitation", { body: { invitationId: id } }));
  };

  const handleReject = async (id: string) => {
    await fetchApi(api.POST("/api/org/rejectInvitation", { body: { invitationId: id } }));
  };

  const SKELETON_COUNT = 2;

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <h2 class="text-2xl font-bold tracking-tight">{t(msg`Invitations`)}</h2>
        <Show when={invitations.isRefetching}>
          <Loader2 class="h-4 w-4 animate-spin text-muted-foreground" />
        </Show>
      </div>

      <Show
        when={!invitations.isLoading}
        fallback={
          <div class="space-y-4">
            <For each={Array.from({ length: SKELETON_COUNT })}>
              {() => <Skeleton class="h-24 w-full rounded-xl" />}
            </For>
          </div>
        }
      >
        <div class="space-y-6">
          <section class="space-y-4">
            <h3 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t(msg`Pending`)}
            </h3>
            <Show
              when={pendingInvitations().length > 0}
              fallback={
                // Emptiness is only asserted when there's NO pending one at all. With
                // invitaciones no aceptables en pantalla, «no tienes ninguna»
                // it would be false and would contradict two blocks below.
                <Show when={allPending().length === 0}>
                  <div class="flex flex-col items-center justify-center rounded-xl border border-dashed p-10 text-center">
                    <Mail class="mb-3 h-8 w-8 text-muted-foreground opacity-20" />
                    <p class="text-sm text-muted-foreground">
                      {t(msg`You have no pending invitations.`)}
                    </p>
                  </div>
                </Show>
              }
            >
              <div class="grid gap-3">
                <For each={pendingInvitations()}>
                  {(inv) => (
                    <InvitationItem
                      invitation={inv}
                      onAccept={handleAccept}
                      onReject={handleReject}
                      onRefresh={() => {
                        void invitations.refetch();
                      }}
                    />
                  )}
                </For>
              </div>
            </Show>
          </section>

          <Show when={unacceptableInvitations().length > 0}>
            <section class="space-y-4" data-testid="invitations-unacceptable">
              <h3 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t(msg`Cannot be accepted`)}
              </h3>
              <p class="text-sm text-muted-foreground">
                {t(
                  msg`This installation works with a single academy, and you already belong to one. You can decline these invitations.`,
                )}
              </p>
              <div class="grid gap-3">
                <For each={unacceptableInvitations()}>
                  {(inv) => (
                    <Card class="border-dashed">
                      <CardContent class="flex flex-wrap items-center gap-4 p-4">
                        <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                          <Mail class="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div class="flex-1 min-w-0">
                          <h4 class="font-bold truncate">{inv.organizationName}</h4>
                          <p class="text-xs text-muted-foreground">
                            {(() => {
                              const email = inv.inviterEmail ?? "";
                              return t(msg`Invited by ${email}`);
                            })()}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          class="gap-2 text-destructive hover:bg-destructive/5 hover:text-destructive"
                          data-testid="invitation-decline-only"
                          onClick={() => {
                            void (async () => {
                              await handleReject(inv.id);
                              await invitations.refetch();
                            })();
                          }}
                        >
                          <XCircle class="h-4 w-4" />
                          {t(msg`Reject`)}
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </For>
              </div>
            </section>
          </Show>

          <Show when={historyInvitations().length > 0}>
            <section class="space-y-4">
              <h3 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t(msg`History`)}
              </h3>
              <div class="grid gap-3 opacity-60">
                <For each={historyInvitations()}>
                  {(inv) => (
                    <Card class="border-none bg-muted/50">
                      <CardContent class="flex items-center gap-4 p-4">
                        <div class="flex h-10 w-10 items-center justify-center rounded-full bg-background">
                          <Archive class="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div class="flex-1">
                          <h4 class="font-bold">{inv.organizationName}</h4>
                          <p class="text-xs text-muted-foreground">
                            {(() => {
                              const email = inv.inviterEmail ?? "";
                              return t(msg`Invited by ${email}`);
                            })()}
                          </p>
                        </div>
                        <Badge variant={inv.status === "accepted" ? "outline" : "destructive"}>
                          {inv.status === "accepted" ? t(msg`Accepted`) : t(msg`Rejected`)}
                        </Badge>
                      </CardContent>
                    </Card>
                  )}
                </For>
              </div>
            </section>
          </Show>
        </div>
      </Show>
    </div>
  );
}

function InvitationItem(props: {
  invitation: Invitation;
  onAccept: (id: string) => Promise<void>;
  onReject: (id: string) => Promise<void>;
  onRefresh: () => void;
}) {
  const { t } = useI18n();
  const actorRef = useActorRef(
    getInvitationMachineLogic({
      acceptInvitation: async (id) => props.onAccept(id),
      rejectInvitation: async (id) => props.onReject(id),
      onRefresh: () => {
        props.onRefresh();
      },
    }),
    { input: { id: untrack(() => props.invitation.id) } },
  );
  const state = fromActorRef(actorRef);
  const { send } = actorRef;

  const isProcessing = () => state().value === "accepting" || state().value === "rejecting";

  return (
    <Card class="border-primary/20 shadow-sm transition-all hover:shadow-md">
      <CardContent class="p-4">
        <div class="flex items-start gap-4">
          <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Mail class="h-6 w-6" />
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <h4 class="font-bold truncate text-lg">{props.invitation.organizationName}</h4>
              <Badge variant="secondary" class="text-[10px] uppercase">
                {props.invitation.role}
              </Badge>
            </div>
            <p class="text-sm text-muted-foreground">
              {t(msg`Invited by`)}{" "}
              <span class="font-medium text-foreground">{props.invitation.inviterEmail}</span>
            </p>
            <p class="mt-1 text-[10px] text-muted-foreground">
              {(() => {
                const date = new Date(props.invitation.expiresAt).toLocaleDateString();
                return t(msg`Expires on ${date}`);
              })()}
            </p>
          </div>
        </div>

        <div class="mt-6 flex gap-3">
          <Button
            class="flex-1 gap-2"
            onClick={() => {
              send({ type: "ACCEPT" });
            }}
            disabled={isProcessing()}
          >
            <Show when={state().value === "accepting"} fallback={<CheckCircle class="h-4 w-4" />}>
              <Loader2 class="h-4 w-4 animate-spin" />
            </Show>
            {t(msg`Accept`)}
          </Button>
          <Button
            variant="outline"
            class="flex-1 gap-2 text-destructive hover:bg-destructive/5 hover:text-destructive"
            onClick={() => {
              send({ type: "REJECT" });
            }}
            disabled={isProcessing()}
          >
            <Show when={state().value === "rejecting"} fallback={<XCircle class="h-4 w-4" />}>
              <Loader2 class="h-4 w-4 animate-spin" />
            </Show>
            {t(msg`Reject`)}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
